import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Filter, ChevronDown, Clock, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import AppShell from '@/components/AppShell';
import { gradeAccent } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gradeLabels = {
  7: 'Kansas History (7th)',
  8: 'U.S. History (8th)',
  9: 'World History',
  10: 'Modern World History',
  11: 'US History (11th)',
  12: 'Government & Economics'
};

const gradeBadgeStyle = (g) => {
  const a = gradeAccent(g);
  return { color: a.hex, background: a.hex + '18', borderColor: a.hex + '40' };
};

export default function HistoricalTimeline() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrades, setSelectedGrades] = useState([7, 8, 9, 10, 11, 12]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedEvent, setExpandedEvent] = useState(null);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/timeline/events`);
      setEvents(res.data.events || []);
    } catch (err) {
      toast.error('Failed to load timeline events');
    } finally {
      setLoading(false);
    }
  };

  const toggleGrade = (grade) => {
    setSelectedGrades(prev =>
      prev.includes(grade) ? prev.filter(g => g !== grade) : [...prev, grade]
    );
  };

  const filteredEvents = events.filter(e => {
    if (!selectedGrades.includes(e.grade_level)) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return e.name.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q) || e.unit_title?.toLowerCase().includes(q);
    }
    return true;
  });

  // Group events by unit for display
  const groupedByUnit = {};
  filteredEvents.forEach(e => {
    const key = `${e.grade_level}-${e.unit_number}`;
    if (!groupedByUnit[key]) {
      groupedByUnit[key] = { grade_level: e.grade_level, unit_number: e.unit_number, unit_title: e.unit_title, unit_id: e.unit_id, events: [] };
    }
    groupedByUnit[key].events.push(e);
  });

  const sortedGroups = Object.values(groupedByUnit).sort((a, b) => {
    if (a.grade_level !== b.grade_level) return a.grade_level - b.grade_level;
    return a.unit_number - b.unit_number;
  });

  if (loading) return <AppShell><div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent" /></div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <div className="mb-6">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Resources</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#14213D] tracking-tight">Historical Timeline</h1>
          <p className="text-sm text-[#5A6577] mt-2 max-w-2xl">Events across every grade — grouped by unit, filterable by grade and free-text search.</p>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-8 border-[#EBE7E0] bg-[#F9F8F4]">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="h-4 w-4 text-[#7A93AC]" />
            <span className="text-sm text-[#5A6577]">Filter by grade:</span>
            {[7,8,9,10,11,12].map(g => {
              const a = gradeAccent(g);
              return (
              <button
                key={g}
                onClick={() => toggleGrade(g)}
                className="text-xs px-3 py-1.5 rounded-full border transition-colors"
                style={selectedGrades.includes(g)
                  ? gradeBadgeStyle(g)
                  : { color: '#718096', background: 'white', borderColor: '#EBE7E0' }}
              >
                Grade {g}
              </button>
              );
            })}
            <div className="flex-1 min-w-[200px] ml-auto">
              <input
                type="text"
                placeholder="Search events…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full rounded-md border border-[#EBE7E0] bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#14213D]/20"
              />
            </div>
          </div>
        </Card>

        <p className="text-sm text-[#5A6577] mb-6">{filteredEvents.length} events across {sortedGroups.length} units</p>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#EBE7E0]" />

          <div className="space-y-8">
            {sortedGroups.map((group) => (
              <div key={`${group.grade_level}-${group.unit_number}`} className="relative">
                {/* Unit Header */}
                <div className="flex items-center gap-4 mb-4 ml-12">
                  <span className="text-xs px-2 py-1 rounded border" style={gradeBadgeStyle(group.grade_level)}>
                    Grade {group.grade_level}
                  </span>
                  <h3 className="font-serif-display text-[#14213D]">Unit {group.unit_number}: {group.unit_title}</h3>
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/unit/${group.unit_id}`)}>
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>

                {/* Events */}
                <div className="space-y-3">
                  {group.events.map((event) => {
                    const eventKey = `${event.grade_level}-${event.unit_number}-${event.name}`;
                    const a = gradeAccent(event.grade_level);
                    return (
                    <div key={eventKey} className="relative flex items-start gap-4">
                      {/* Dot */}
                      <div className="w-3 h-3 rounded-full mt-2 z-10 ml-[18px] ring-2 ring-white" style={{ background: a.hex }} />

                      {/* Event Card */}
                      <Card
                        className="flex-1 p-4 cursor-pointer hover:shadow-md transition-shadow border-[#EBE7E0]"
                        onClick={() => setExpandedEvent(expandedEvent === eventKey ? null : eventKey)}
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-[#14213D]">{event.name}</h4>
                          <ChevronDown className={`h-4 w-4 text-[#5A6577] transition-transform ${expandedEvent === eventKey ? 'rotate-180' : ''}`} />
                        </div>

                        {expandedEvent === eventKey && (
                          <div className="mt-3 pt-3 border-t border-[#EBE7E0]">
                            <p className="text-sm text-[#2D3748] mb-2">{event.description}</p>
                            {event.significance && (
                              <div className="mb-2">
                                <span className="text-xs font-medium text-[#5A6577]">Significance: </span>
                                <span className="text-sm text-[#2D3748]">{event.significance}</span>
                              </div>
                            )}
                            {event.connections?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {event.connections.map((c) => (
                                  <span key={c} className="text-xs bg-[#E2E8F0]/60 text-[#2D3748] px-2 py-0.5 rounded">{c}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </Card>
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {sortedGroups.length === 0 && (
          <Card className="p-12 text-center border-[#EBE7E0]">
            <Clock className="h-12 w-12 text-[#E2E8F0] mx-auto mb-4" />
            <h3 className="font-serif-display text-xl text-[#14213D] mb-2">No events found</h3>
            <p className="text-[#5A6577]">Adjust your filters or search to find events.</p>
          </Card>
        )}
      </div>
    </AppShell>
  );
}
